/** Practice tile art — the viobean-style illustrated cards.
 *
 * Until every item carries a real `image_url`, each item gets a
 * DETERMINISTIC tile: a saturated gradient wash (theme tokens) + a
 * hand-drawn monument vector in one of the two approved colourways.
 * Same slug → same art on every device, so the wall feels designed, not
 * random. Monuments cycle through the drawn set; as the set grows, tile
 * variety grows automatically. When an item's `image_url` is filled the
 * grid swaps to the image — this is the fallback layer.
 */
import type { ColorwayKey } from "./monuments/frame";
import { DRAWN_MONUMENT_KEYS } from "./monuments/registry";

export const ART_GRADIENT_COUNT = 6;

/** Small stable string hash (djb2) — art must not change between visits. */
export function hashString(value: string): number {
  let hash = 5381;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 33) ^ value.charCodeAt(i);
  }
  return Math.abs(hash);
}

export interface TileArt {
  /** 1-based index into the --art-N-from/to token pairs. */
  gradient: number;
  /** A drawn monument key (registry-backed). */
  monumentKey: string;
  colorway: ColorwayKey;
}

/** Cool/green washes (1,4,5) carry the WARM sand+gold icon; warm washes
 * (2,3,6) carry the COOL cobalt icon — so the monument always
 * contrasts its background instead of blending into it. */
const COOL_WASHES = new Set([1, 4, 5]);

export function tileArtFor(slug: string): TileArt {
  const hash = hashString(slug);
  const keys = DRAWN_MONUMENT_KEYS;
  const gradient = (hash % ART_GRADIENT_COUNT) + 1;
  return {
    gradient,
    monumentKey: keys[hash % keys.length],
    colorway: COOL_WASHES.has(gradient) ? "sandGold" : "cobalt",
  };
}

/** CSS background value for a gradient index — colors stay in tokens.css.
 * A soft top vignette adds depth so the lit monument stands off the wash. */
export function tileBackground(gradient: number): string {
  return (
    "radial-gradient(115% 80% at 50% 16%, rgba(0,0,0,0.06), transparent 62%), " +
    `linear-gradient(160deg, rgb(var(--art-${gradient}-from)), rgb(var(--art-${gradient}-to)))`
  );
}
